import type {
  MapCloudPoint,
  MapPointCloud,
  MapPointCloudSetOptions,
} from '@gpx-spa/geo-domain'
import type { Feature, FeatureCollection, Point } from 'geojson'
import L from 'leaflet'
import type { GlifyLayer } from 'leaflet.glify'
import type { MapInteractionController } from '../shared/map-interaction-controller.js'
import { resolvePointCloudThinning } from '../shared/thin-point-cloud-by-min-gap.js'
import { getLeafletGlify, glifyHoverCursor, silenceGlifyPointer } from './leaflet-glify-api.js'
import type { LeafletHitRouter } from './leaflet-hit-router.js'
import { cssColorToGlify, glifyPointFillRadius } from './leaflet-glify-color.js'
import { TRACK_VERTEX_UNDER_MARKERS_PANE } from './leaflet-constants.js'

const DEFAULT_RADIUS = 6
const DEFAULT_BORDER_WIDTH = 2

export type LeafletGlifyPointCloudLayerOptions = {
  layerId: string
  getInteractionController?: () => MapInteractionController | null
  getHitRouter?: () => LeafletHitRouter | null
  onPointClick?: (point: MapCloudPoint, latlng: L.LatLng, pointIndex: number) => void
  onPointHit?: (point: MapCloudPoint) => void
}

type IndexedPoint = MapCloudPoint & { index: number }

type CloudFeatureProps = {
  __gpx_point_index: number
  __gpx_fill: string
  __gpx_radius: number
  __gpx_border_width: number
}

function ensureTrackVertexPane(map: L.Map): void {
  if (!map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE)) {
    map.createPane(TRACK_VERTEX_UNDER_MARKERS_PANE)
    const pane = map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE)
    if (pane) pane.style.zIndex = '655'
  }
}

function pointsToFeatureCollection(points: IndexedPoint[]): FeatureCollection<Point> {
  return {
    type: 'FeatureCollection',
    features: points.map((p) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [p.position.lng, p.position.lat],
      },
      properties: {
        __gpx_point_index: p.index,
        __gpx_fill: p.style.fillColor,
        __gpx_radius: p.style.radius ?? DEFAULT_RADIUS,
        __gpx_border_width: p.style.borderWidth ?? DEFAULT_BORDER_WIDTH,
      } satisfies CloudFeatureProps,
    })),
  }
}

/**
 * White ring on the same point sprite. Alpha is the fill radius in sprite space
 * (outer edge is 0.5), not opacity. One context: a second points layer is not used.
 */
const POINT_FRAGMENT_SHADER = `
precision mediump float;
varying vec4 _color;

void main() {
  vec2 uv = gl_PointCoord.xy - vec2(0.5);
  float dist = length(uv);
  float fillRadius = clamp(_color.a, 0.0, 0.5);
  float edge = 0.02;
  if (dist > 0.5 + edge) discard;
  float cover = 1.0 - smoothstep(0.5 - edge, 0.5 + edge, dist);
  float fill = 1.0 - smoothstep(fillRadius - edge, fillRadius + edge, dist);
  vec4 ring = vec4(1.0, 1.0, 1.0, cover);
  gl_FragColor = mix(ring, vec4(_color.rgb, cover), fill);
}
`

/**
 * WebGL point-cloud via leaflet.glify (one context per cloud layer).
 * Click-only — no hover/mousemove.
 */
export class LeafletGlifyPointCloudLayer extends L.Layer {
  private fullPoints: MapCloudPoint[] = []
  private points: IndexedPoint[] = []
  private thinningOptions: MapPointCloudSetOptions | undefined
  private pointsGl: GlifyLayer | null = null
  /** Latest FeatureCollection — size/color callbacks read this after setData. */
  private collection: FeatureCollection<Point> | null = null
  private readonly cursor: ReturnType<typeof glifyHoverCursor>
  private readonly testerName: string

  constructor(private readonly opts: LeafletGlifyPointCloudLayerOptions) {
    super()
    this.testerName = `point-cloud:${opts.layerId}`
    this.cursor = glifyHoverCursor(() => this.opts.getHitRouter?.() ?? null, this.testerName)
  }

  setPointCloud(cloud: MapPointCloud | null, options?: MapPointCloudSetOptions): void {
    this.fullPoints = cloud?.points ? [...cloud.points] : []
    if (options !== undefined) {
      this.thinningOptions = options
    }
    this.applyVisibleFromFull()
  }

  getPointCloud(): MapPointCloud {
    return { points: this.fullPoints }
  }

  hitAt(containerPoint: L.Point): boolean {
    const map = this._map
    if (!map) return false
    for (const p of this.points) {
      const pt = map.latLngToContainerPoint(L.latLng(p.position.lat, p.position.lng))
      const slop = (p.style.radius ?? DEFAULT_RADIUS) + 4
      if (Math.hypot(pt.x - containerPoint.x, pt.y - containerPoint.y) <= slop) return true
    }
    return false
  }

  onAdd(map: L.Map): this {
    ensureTrackVertexPane(map)
    map.on('zoomend', this.onZoomEnd, this)
    this.opts.getHitRouter?.()?.register({
      name: this.testerName,
      priority: 100,
      hitAt: (point) => this.hitAt(point),
    })
    this.applyVisibleFromFull()
    return this
  }

  onRemove(map: L.Map): this {
    this.opts.getHitRouter?.()?.unregister(this.testerName)
    map.off('zoomend', this.onZoomEnd, this)
    this.destroyGl()
    return this
  }

  private onZoomEnd = (): void => {
    this.applyVisibleFromFull()
  }

  private destroyGl(): void {
    this.pointsGl?.remove()
    this.pointsGl = null
    this.collection = null
  }

  private applyVisibleFromFull(): void {
    const map = this._map
    if (!map) return
    const zoom = map.getZoom()
    const visible = resolvePointCloudThinning(
      this.fullPoints,
      zoom,
      this.thinningOptions,
    )
    this.points = visible.map((p, index) => ({ ...p, index }))
    this.rebuildGl(map)
  }

  private rebuildGl(map: L.Map): void {
    if (this.points.length === 0) {
      this.destroyGl()
      return
    }

    this.collection = pointsToFeatureCollection(this.points)

    if (this.pointsGl) {
      this.pointsGl.setData(this.collection)
      return
    }

    const glify = getLeafletGlify()
    this.pointsGl = glify.points({
      map,
      data: this.collection,
      pane: TRACK_VERTEX_UNDER_MARKERS_PANE,
      className: 'leaflet-glify-point-cloud',
      opacity: 1,
      sensitivity: 1.2,
      fragmentShaderSource: POINT_FRAGMENT_SHADER,
      size: (i) => {
        const props = this.collection?.features[i]?.properties as CloudFeatureProps | undefined
        const r = props?.__gpx_radius ?? DEFAULT_RADIUS
        const bw = props?.__gpx_border_width ?? DEFAULT_BORDER_WIDTH
        return 2 * (r + bw)
      },
      color: (i) => {
        const props = this.collection?.features[i]?.properties as CloudFeatureProps | undefined
        const r = props?.__gpx_radius ?? DEFAULT_RADIUS
        const bw = props?.__gpx_border_width ?? DEFAULT_BORDER_WIDTH
        return cssColorToGlify(props?.__gpx_fill ?? '#888888', glifyPointFillRadius(r, bw))
      },
      click: (e, feature) => {
        this.handleClick(e, feature)
        return true
      },
      hover: () => this.cursor.hover(),
      hoverOff: () => this.cursor.hoverOff(),
    })
    silenceGlifyPointer(this.pointsGl)
  }

  private handleClick(e: L.LeafletMouseEvent, feature: unknown): void {
    const f = feature as Feature<Point>
    const idx = (f.properties as CloudFeatureProps | null)?.__gpx_point_index
    const indexed = typeof idx === 'number' ? this.points.find((p) => p.index === idx) : undefined
    if (!indexed) return

    L.DomEvent.stopPropagation(e)
    this.opts.onPointHit?.(indexed)

    const controller = this.opts.getInteractionController?.()
    if (controller?.hasInteractions()) {
      void controller.dispatchHit({
        trigger: 'click',
        latlng: { lat: indexed.position.lat, lng: indexed.position.lng },
        nativeEvent: e,
        target: {
          type: 'pointCloud',
          layerId: this.opts.layerId,
          position: { lat: indexed.position.lat, lng: indexed.position.lng },
          point: indexed,
          pointIndex: indexed.index,
        },
      })
      return
    }

    this.opts.onPointClick?.(indexed, e.latlng, indexed.index)
  }
}

export function createLeafletGlifyPointCloudLayer(
  options: LeafletGlifyPointCloudLayerOptions,
): LeafletGlifyPointCloudLayer {
  return new LeafletGlifyPointCloudLayer(options)
}
